import { type Context, createContext } from 'react';

import { ChipOwnProps } from '../types/chip.js';

export type ChipContextProps = {
  /** Whether the element is disabled. */
  disabled: boolean;
  /** Whether the Chip has a key. */
  hasKey: boolean;
  /** The variant of the Chip, used for styling the prefix/suffix. */
  variant: 'accent' | 'emphasized';
  /** The color of the Chip, used for styling the prefix/suffix. */
  color: Required<ChipOwnProps['color']>;
  /** The size of the Chip, used for styling. */
  size: Required<ChipOwnProps['size']>;
  /** The id of the Chip value container. */
  valueId: string;
};

/**
 * This context keeps all information needed for the chip state.
 * @param disabled - defines if the element is disabled; it's set to false by default
 * @param hasKey - defines if the element has a key; it's set to false by default
 * @param variant - defines the element's variant, used for styling; it's set to emphasized by default
 * @param color - defines the element's color, used for styling; it's set to neutral by default
 * @param size - defines the element's size, used for styling; it's set to default by default
 * @param valueId - defines the id of the Chip value container; it's set to an empty string by default
 */
export const ChipContext: Context<ChipContextProps> =
  createContext<ChipContextProps>({
    disabled: false,
    hasKey: false,
    variant: 'emphasized',
    color: 'neutral',
    size: 'default',
    valueId: '',
  });
